import { Container, Typography, Box, Link, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid2';

function ReimaginingDance() {
  return (
    <Container sx={{ padding: '40px 0' }}>
      <Typography variant="h2" gutterBottom sx={{ textAlign: 'left', marginBottom: '24px' }}>
        Reimagining Dance: Real-time Music Co-creation between Dancers and AI
      </Typography>
      
      <Typography variant="body1" sx={{ fontSize: '1.1rem', marginBottom: '20px', fontStyle: 'italic' }}>
        Published at the International Conference on Computational Creativity (ICCC 2025)
      </Typography>

      <Typography variant="body1" sx={{ fontSize: '1.1rem', marginBottom: '30px' }}>
        This research is supported by the SSHRC Insight Development Grant "Reimagining Dance: Real-time
        Music Co-creation between Dancers and AI" (PI: Olga Vechtomova).
      </Typography>

      {/* Abstract Section */}
      <Box sx={{ marginBottom: '40px' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Abstract
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, textAlign: 'justify' }}>
          Dance performance traditionally follows a unidirectional relationship where movement responds to music. 
          While AI has advanced in various creative domains, its application in dance has primarily focused on 
          generating choreography from musical input. We present a system that enables dancers to dynamically 
          shape musical environments through their movements. Our multi-modal architecture creates a coherent 
          musical composition by intelligently combining pre-recorded musical clips in response to dance movements, 
          establishing a bidirectional creative partnership where dancers function as both performers and composers. 
          Through correlation analysis of performance data, we demonstrate emergent communication patterns between 
          movement qualities and audio features. This approach reconceptualizes the role of AI in performing 
          arts—as a responsive collaborator that expands possibilities for both professional dance performance 
          and improvisational artistic expression across broader populations.
        </Typography>
      </Box>

      {/* Key Features Section */}
      <Box sx={{ marginBottom: '40px' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Key Innovation
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Bidirectional Creativity
                </Typography>
                <Typography variant="body2">
                  Transforms the traditional dance-music relationship from unidirectional (music → dance) 
                  to bidirectional (music ↔ dance), enabling dancers to become active co-creators of the musical landscape.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Real-time Response
                </Typography>
                <Typography variant="body2">
                  Uses multi-modal VAE and GAN architecture to analyze movement trajectories and predict 
                  appropriate musical responses in real-time, creating seamless interactive performances.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Emergent Communication
                </Typography>
                <Typography variant="body2">
                  Statistical analysis reveals meaningful correlations between movement qualities and audio features, 
                  demonstrating emergent communication patterns between dancer and AI system.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Presentation Video Section */}
      <Box sx={{ marginBottom: '40px' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Conference Presentation
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: '20px' }}>
          Watch the full presentation from ICCC 2025:
        </Typography>
        <Box sx={{ 
          position: 'relative', 
          paddingBottom: '56.25%', /* 16:9 aspect ratio */
          height: 0,
          overflow: 'hidden',
          maxWidth: '100%',
          backgroundColor: '#000'
        }}>
          <iframe
            src="https://player.vimeo.com/video/1096261737?h=02c8f07a59&amp;title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            allowFullScreen
            title="Reimagining Dance"
          />
        </Box>
      </Box>

      {/* Slides Section */}
      <Box sx={{ marginBottom: '40px' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Presentation Slides
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: '20px' }}>
          View the presentation slides:
        </Typography>
        <Box sx={{ 
          width: '100%',
          height: '600px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <iframe
            src="/pdf/reimagining-dance-slides.pdf"
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title="Reimagining Dance Slides"
          />
        </Box>
      </Box>

      {/* Paper Section */}
      <Box sx={{ marginBottom: '40px' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Research Paper
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: '20px' }}>
          Read the full research paper:
        </Typography>
        <Box sx={{ 
          width: '100%',
          height: '800px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <iframe
            src="/pdf/reimagining-dance-paper.pdf"
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title="Reimagining Dance Paper"
          />
        </Box>
      </Box>

      {/* Links Section */}
      <Box sx={{ marginBottom: '40px' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Additional Resources
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Link 
              href="https://sites.google.com/view/reimagining-dance" 
              target="_blank" 
              rel="noopener"
              sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
            >
              <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <CardContent>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Project Website
                  </Typography>
                  <Typography variant="body2">
                    Demo videos and supplementary materials
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Link 
              href="/pdf/reimagining-dance-paper.pdf" 
              target="_blank" 
              rel="noopener"
              sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
            >
              <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <CardContent>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Download Paper
                  </Typography>
                  <Typography variant="body2">
                    PDF version of the research paper
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Link 
              href="/pdf/reimagining-dance-slides.pdf" 
              target="_blank" 
              rel="noopener"
              sx={{ display: 'block', padding: '10px', textDecoration: 'none' }}
            >
              <Card sx={{ textAlign: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <CardContent>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Download Slides
                  </Typography>
                  <Typography variant="body2">
                    Presentation slides from ICCC 2025
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
        </Grid>
      </Box>

      {/* Citation Section */}
      <Box sx={{ marginBottom: '40px' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Citation
        </Typography>
        <Box sx={{ 
          backgroundColor: '#f5f5f5', 
          padding: '20px', 
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '0.9rem'
        }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            @inproceedings{`{`}vechtomova2025reimagining,<br />
            &nbsp;&nbsp;title={`{`}Reimagining Dance: Real-time Music Co-creation between Dancers and AI{`}`},<br />
            &nbsp;&nbsp;author={`{`}Vechtomova, Olga and Bos, Jeff{`}`},<br />
            &nbsp;&nbsp;booktitle={`{`}Proceedings of the International Conference on Computational Creativity{`}`},<br />
            &nbsp;&nbsp;year={`{`}2025{`}`}<br />
            {`}`}
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}

export default ReimaginingDance;